import DateRangePicker from '@wojtekmaj/react-daterange-picker/dist/DateRangePicker';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const DateRangerPickerWithCustomTimezone = (props) => {
    const {
        onChange = () => {},
        onBlur = () => {},
        onInvalidRange = () => {},
        value,
        clearIcon,
        lockStartDate = false,
        minDate: minDateProp,
        maxDate,
    } = props;
    const { t } = useTranslation();
    const onInvalidRangeRef = useRef(onInvalidRange);
    // ponytail: minDate frozen at mount; long-lived wizard sessions crossing
    // midnight keep a stale `today`. Reopen the wizard to recompute. Acceptable
    // for the campaign-wizard flow; add a live recompute if it ever bites.
    const defaultMinDate = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    }, []);
    const minDate = minDateProp ?? defaultMinDate;

    const isPastDate = (date) => {
        if (!date) return false;
        return new Date(date).getTime() < minDate.getTime();
    };

    const isInvalidRange = (periodValue) => {
        if (!Array.isArray(periodValue) || periodValue.length < 2) {
            return false;
        }

        return periodValue.some((date) => isPastDate(date));
    };

    useEffect(() => {
        onInvalidRangeRef.current = onInvalidRange;
    }, [onInvalidRange]);

    useEffect(() => {
        if (value && isInvalidRange(value)) {
            onInvalidRangeRef.current(value);
        }
    }, [value, minDate]);

    const handleOnChange = (periodValue) => {
        // lockStartDate: spent campaigns keep their start fixed; the calendar
        // still shows clickable days, but we drop any selection that moves the
        // start away from the value loaded by Timing. Timing's safety net in
        // handleChangePeriodValue still re-checks, this just avoids firing.
        if (lockStartDate && value && periodValue) {
            const loadedStart = value[0];
            const newStart = Array.isArray(periodValue)
                ? periodValue[0]
                : null;
            if (loadedStart && newStart) {
                const loadedStartStamp = new Date(
                    loadedStart
                ).setHours(0, 0, 0, 0);
                const newStartStamp = new Date(newStart).setHours(0, 0, 0, 0);
                if (newStartStamp !== loadedStartStamp) {
                    onInvalidRangeRef.current(periodValue);
                    return;
                }
            }
        }
        onChange(periodValue);
    };

    return (
        <div data-testid="date-range">
            <DateRangePicker
                clearIcon={clearIcon}
                rangeDivider={t('common.components.DateRangePicker.until')}
                onChange={handleOnChange}
                value={value ?? null}
                className={props.className}
                onBlur={onBlur}
                minDate={minDate}
                maxDate={maxDate}
            />
        </div>
    );
};

export default DateRangerPickerWithCustomTimezone;
